"use client"

import { forwardRef, useRef, useEffect, useMemo } from "react"
import type { ElementType } from "react"
import { cn } from "@/lib/utils"
import { useSidebarContext } from "./hooks/useSidebarContext"
import { useSubContent } from "./hooks/useSubContent"
import { useSidebarIndicator } from "./hooks/useSidebarIndicator"
import { useSidebarKeyboard } from "./hooks/useSidebarKeyboard"
import { useSidebarLiquidGlass } from "@/hooks/use-sidebar-liquid-glass"
import { SidebarFluidIndicator } from "./SidebarFluidIndicator"
import { SidebarTooltip } from "./SidebarTooltip"
import type { SidebarNavProps } from "./Sidebar.types"
import {
  SIDEBAR_TAILWIND_CLASSES,
  SIDEBAR_ARIA_LABELS,
  SIDEBAR_DATA_ATTRIBUTES,
  SIDEBAR_CSS_VARIABLES,
  DEFAULT_DIMENSIONS,
  DEFAULT_ANIMATIONS,
  DEFAULT_VISUAL_STYLE,
} from "./Sidebar.constants"
import { generateSidebarStyles } from "./Sidebar.styles"

/**
 * Componente de navegación del Sidebar
 *
 * @description
 * Contenedor de navegación que renderiza un elemento `<nav>` por defecto.
 * Gestiona los eventos de mouse y teclado para el indicador visual y
 * la navegación accesible.
 *
 * Este componente:
 * - Aplica data attributes para reflejar el estado (open/collapsed)
 * - Gestiona eventos de mouse para el indicador visual
 * - Gestiona navegación por teclado (arrows, home, end)
 * - Expone una ref al elemento DOM para mediciones
 *
 * Usa forwardRef para permitir acceso al elemento DOM desde componentes padre.
 *
 * @example
 * ```tsx
 * <Sidebar>
 *   <SidebarNav className="bg-zinc-900 border-zinc-800">
 *     <SidebarToggle />
 *     <SidebarIndicator />
 *     <SidebarContent>
 *       <SidebarItem href="/" />
 *     </SidebarContent>
 *   </SidebarNav>
 * </Sidebar>
 * ```
 *
 * @example
 * ```tsx
 * // Con data attributes para styling
 * <SidebarNav className="data-[open=true]:w-64 data-[open=false]:w-16">
 *   {children}
 * </SidebarNav>
 * ```
 *
 * @example
 * ```tsx
 * // Con elemento personalizado
 * <SidebarNav as="aside" aria-label="Navegación lateral">
 *   {children}
 * </SidebarNav>
 * ```
 */
export const SidebarNav = forwardRef<HTMLElement, SidebarNavProps>(function SidebarNav(
  { children, className = "", style, "aria-label": ariaLabel, as, ...props },
  forwardedRef,
) {
  const {
    defaultOpen,
    navRef,
    indicatorRef,
    dimensions,
    animations,
    collapseMode,
    hideBehaviour,
    hideOpensBehavior,
    layoutBehaviour,
    toggleId,
    enableFluidIndicator,
    enableTooltip,
    debug,
    liquidGlass,
  } = useSidebarContext()

  // Ref local para el nav (DEBE estar ANTES del hook condicional)
  const localNavRef = useRef<HTMLElement>(null)

  // Generar liquid glass effect para sidebar (SIEMPRE llamar el hook)
  const sidebarGlass = useSidebarLiquidGlass('sidebar', {
    enableChromaticAberration: liquidGlass.enableChromaticAberration,
    ...liquidGlass.presetOverrides // Aplicar overrides configurables desde UI
  })

  // Combinar refs en useEffect
  useEffect(() => {
    const node = localNavRef.current
    if (!node) return

    // Asignar a context ref
    if (navRef && "current" in navRef) {
      ;(navRef as React.MutableRefObject<HTMLElement | null>).current = node
    }

    // Asignar a forwarded ref
    if (typeof forwardedRef === "function") {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }, [navRef, forwardedRef])

  // Note: Liquid glass styles are now applied via CSS layers and data-liquid-glass attribute
  // No need for useEffect manipulation of inline styles

  // Hooks para indicador y navegación por teclado
  const {
    isVisible,
    hoveredItemId,
    hoveredLabel,
    currentItemElement,
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    clearHoveredState,
  } = useSidebarIndicator()

  // Obtener subContent desde el context
  const { getSubContent } = useSubContent()
  const subContentData = hoveredItemId ? getSubContent(hoveredItemId) : null

  useSidebarKeyboard()

  // Generar estilos CSS con tokens de shadcn (memoizado para regenerar cuando cambian dimensions/animations)
  const sidebarStyles = useMemo(
    () =>
      generateSidebarStyles({
        dimensions: { ...DEFAULT_DIMENSIONS, ...dimensions },
        animations: { ...DEFAULT_ANIMATIONS, ...animations },
        collapseMode,
        hideOpensBehavior,
        layoutBehaviour,
        visualStyle: { ...DEFAULT_VISUAL_STYLE, ...dimensions.visualStyle },
      }),
    [dimensions, animations, collapseMode, hideOpensBehavior, layoutBehaviour, dimensions.visualStyle],
  )

  // Elemento a renderizar (default: nav)
  const Component = (as || "nav") as ElementType

  // Lógica para mostrar el toggle button
  const shouldShowToggle =
    collapseMode === "collapse" || hideBehaviour === "showIndicator"

  // Key para forzar regeneración de estilos cuando cambien dimensions
  const styleKey = useMemo(
    () => `${dimensions.collapsedWidth}-${dimensions.expandedWidth}-${layoutBehaviour}-${JSON.stringify(dimensions.visualStyle || {})}`,
    [dimensions, layoutBehaviour, dimensions.visualStyle],
  )

  return (
    <>
      {/* Inyectar estilos CSS generados con key para forzar regeneración */}
      <style key={styleKey} dangerouslySetInnerHTML={{ __html: sidebarStyles }} />

      {/* Wrapper con CSS variables y estado */}
      <div
        className={`sidebar-wrapper ${SIDEBAR_TAILWIND_CLASSES.wrapper} ${className}`}
        style={style}
        data-layout={layoutBehaviour}
      >
        {/* Checkbox oculto NO controlado - CSS puro con :has() */}
        <input
          type="checkbox"
          id={toggleId}
          className="sr-only"
          defaultChecked={defaultOpen}
          aria-label="Toggle sidebar"
        />

        {/* Toggle button visual (FUERA del nav) */}
        {shouldShowToggle && (
          <div className={`toggle-button-wrapper ${SIDEBAR_TAILWIND_CLASSES.toggleButtonWrapper}`}>
            <label
              htmlFor={toggleId}
              className={`${SIDEBAR_TAILWIND_CLASSES.toggleButton} bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 ${
                collapseMode === "hide" && hideBehaviour === "showIndicator" ? "shadow-lg" : ""
              }`}
              aria-label="Toggle sidebar"
            >
              {/* Icono chevron SVG */}
              <svg
                className="toggle-icon w-3 h-3 text-zinc-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {collapseMode === "hide" ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                )}
              </svg>
            </label>
          </div>
        )}

        {/* Debug: Mostrar si liquid glass está activo */}
        {debug && liquidGlass.enableLiquidGlassV2 && (
          <div className="absolute top-2 right-2 z-50 px-2 py-1 bg-purple-500/90 text-white text-xs rounded font-mono">
            🧪 Liquid Glass V2 Active
          </div>
        )}

        {/* SVG Filter for Liquid Glass V2 */}
        {liquidGlass.enableLiquidGlassV2 && (
          <svg className="absolute inset-0 pointer-events-none w-0 h-0" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="sidebar-liquid-glass" colorInterpolationFilters="sRGB">
                {/* Displacement Map generado con refracción física */}
                <feImage
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  result="map"
                  href={sidebarGlass.dataUri}
                />

                {/* Phase 1: Legacy chromatic aberration con scale offsets */}
                {!liquidGlass.enableChromaticAberration && (
                  <>
                    {/* Red channel */}
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="map"
                      xChannelSelector={sidebarGlass.filterAttributes.red.xChannelSelector}
                      yChannelSelector={sidebarGlass.filterAttributes.red.yChannelSelector}
                      scale={sidebarGlass.filterAttributes.red.scale}
                      result="dispRed"
                    />
                    <feColorMatrix
                      in="dispRed"
                      type="matrix"
                      values="1 0 0 0 0
                              0 0 0 0 0
                              0 0 0 0 0
                              0 0 0 1 0"
                      result="red"
                    />

                    {/* Green channel */}
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="map"
                      xChannelSelector={sidebarGlass.filterAttributes.green.xChannelSelector}
                      yChannelSelector={sidebarGlass.filterAttributes.green.yChannelSelector}
                      scale={sidebarGlass.filterAttributes.green.scale}
                      result="dispGreen"
                    />
                    <feColorMatrix
                      in="dispGreen"
                      type="matrix"
                      values="0 0 0 0 0
                              0 1 0 0 0
                              0 0 0 0 0
                              0 0 0 1 0"
                      result="green"
                    />

                    {/* Blue channel */}
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="map"
                      xChannelSelector={sidebarGlass.filterAttributes.blue.xChannelSelector}
                      yChannelSelector={sidebarGlass.filterAttributes.blue.yChannelSelector}
                      scale={sidebarGlass.filterAttributes.blue.scale}
                      result="dispBlue"
                    />
                    <feColorMatrix
                      in="dispBlue"
                      type="matrix"
                      values="0 0 0 0 0
                              0 0 0 0 0
                              0 0 1 0 0
                              0 0 0 1 0"
                      result="blue"
                    />

                    {/* Blend RGB channels */}
                    <feBlend in="red" in2="green" mode="screen" result="rg" />
                    <feBlend in="rg" in2="blue" mode="screen" result="output" />

                    {/* Final blur */}
                    <feGaussianBlur
                      in="output"
                      stdDeviation={sidebarGlass.filterAttributes.gaussianBlur.stdDeviation}
                    />
                  </>
                )}

                {/* Phase 2: True chromatic aberration con feColorMatrix + feOffset */}
                {liquidGlass.enableChromaticAberration && sidebarGlass.chromaticAberrationSVG && (
                  <>
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="map"
                      xChannelSelector={sidebarGlass.filterAttributes.red.xChannelSelector}
                      yChannelSelector={sidebarGlass.filterAttributes.red.yChannelSelector}
                      scale={sidebarGlass.filterAttributes.red.scale}
                      result="displaced"
                    />
                    <g dangerouslySetInnerHTML={{ __html: sidebarGlass.chromaticAberrationSVG }} />
                    <feGaussianBlur
                      in="chromaticAberration"
                      stdDeviation={sidebarGlass.filterAttributes.gaussianBlur.stdDeviation}
                    />
                  </>
                )}
              </filter>
            </defs>
          </svg>
        )}

        {/* Nav interno con eventos */}
        <Component
          ref={localNavRef}
          className={`sidebar-nav ${SIDEBAR_TAILWIND_CLASSES.nav}`}
          data-liquid-glass={liquidGlass.enableLiquidGlassV2 ? "true" : "false"}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={ariaLabel || SIDEBAR_ARIA_LABELS.mainNavigation}
          {...props}
        >
          {/* Backdrop extension layer - solo si liquid glass V2 está activo */}
          {liquidGlass.enableLiquidGlassV2 && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                height: '200%',
                backdropFilter: 'blur(8px) url(#sidebar-liquid-glass) brightness(1.1) saturate(1.5)',
                WebkitBackdropFilter: 'blur(8px) url(#sidebar-liquid-glass) brightness(1.1) saturate(1.5)',
                maskImage: 'linear-gradient(to bottom, black 0% 50%, transparent 50% 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 0% 50%, transparent 50% 100%)',
                zIndex: 0
              }}
            />
          )}

          {/* Indicador lateral (barra vertical - SIEMPRE visible) */}
          <div
            ref={indicatorRef}
            className={`sidebar-indicator ${SIDEBAR_TAILWIND_CLASSES.indicator} pointer-events-none`}
            style={{
              background: "var(--sidebar-primary)",
              transform: "translateY(-50%)",
              opacity: isVisible ? 1 : 0,
              height: `var(${SIDEBAR_CSS_VARIABLES.indicatorHeight})`,
              width: "0.25rem",
              anchorName: "--sidebar-indicator",
            }}
            aria-hidden="true"
          />


          {/* Líneas horizontales de threshold dinámicas - marcan bordes del elemento hovered */}
          {debug && currentItemElement && navRef.current && (() => {
            const navRect = navRef.current!.getBoundingClientRect()
            const itemRect = currentItemElement.getBoundingClientRect()
            const top = itemRect.top - navRect.top
            const height = itemRect.height

            return (
              <>
                <style dangerouslySetInnerHTML={{
                  __html: `
                    @keyframes threshold-pulse {
                      0%, 100% {
                        opacity: 0.6;
                        transform: scaleX(0.95);
                      }
                      50% {
                        opacity: 1;
                        transform: scaleX(1);
                      }
                    }
                  `
                }} />

                {/* Línea TOP - marca donde empieza el elemento */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: `${top}px`,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(251, 191, 36, 0.9) 10%, rgba(251, 191, 36, 0.9) 90%, transparent 100%)',
                    zIndex: 200,
                    animation: 'threshold-pulse 2s ease-in-out infinite',
                  }}
                >
                  {/* Label TOP */}
                  <div
                    className="absolute -top-4 right-2 text-[8px] text-amber-300 font-mono bg-black/90 px-1.5 py-0.5 rounded whitespace-nowrap"
                  >
                    TOP: {Math.round(top)}px
                  </div>
                </div>

                {/* Línea BOTTOM - marca donde termina el elemento */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: `${top + height}px`,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(239, 68, 68, 0.9) 10%, rgba(239, 68, 68, 0.9) 90%, transparent 100%)',
                    zIndex: 200,
                    animation: 'threshold-pulse 2s ease-in-out infinite 1s',
                  }}
                >
                  {/* Label BOTTOM */}
                  <div
                    className="absolute -bottom-4 right-2 text-[8px] text-red-300 font-mono bg-black/90 px-1.5 py-0.5 rounded whitespace-nowrap"
                  >
                    BOTTOM: {Math.round(top + height)}px (H: {Math.round(height)}px)
                  </div>
                </div>
              </>
            )
          })()}

          {/* Fluid Indicator glassmorphism (OPCIONAL - controlado desde App.tsx) */}
          {enableFluidIndicator && (
            <SidebarFluidIndicator targetElement={currentItemElement} isVisible={isVisible} />
          )}

          {/* Estructura de columna flex con safe areas y content */}
          <div className="relative z-10 h-full flex flex-col">
            {children}
          </div>
        </Component>
      </div>

      {/* Tooltip anclado al indicador - fuera del nav para z-index correcto */}
      {enableTooltip && (
        <>
          {/* Tooltip con subContent (links) - Unificado con feature flags opcionales */}
          {subContentData && (
            <SidebarTooltip
              title={subContentData.title}
              debug={debug}
              onMouseLeave={clearHoveredState}
              enableLiquidGlass={liquidGlass.enableLiquidGlassV2}
              enableEdgeGlass3D={liquidGlass.enableEdgeGlass3D}
              enableBackdropExtension={liquidGlass.enableBackdropExtension}
              enableChromaticAberration={liquidGlass.enableChromaticAberration}
              enableTooltipItemAnimations={liquidGlass.enableTooltipItemAnimations}
              enableCylinderTitle={liquidGlass.enableCylinderTitle}
            >
              {subContentData.subContent}
            </SidebarTooltip>
          )}

          {/* Tooltip simple para items sin subContent */}
          {!subContentData && hoveredLabel && (
            <>
              <style dangerouslySetInnerHTML={{
                __html: `
                  @keyframes activatePointerEventsSimple {
                    to { pointer-events: all; }
                  }

                  .sidebar-tooltip-simple::before {
                    content: '';
                    position: absolute;
                    right: 100%;
                    top: 0;
                    width: calc(var(--sidebar-tooltip-distance) + 0.25rem + 2px);
                    height: 100%;
                    clip-path: polygon(0% 25%, 100% 0%, 100% 100%, 0% 75%);
                    pointer-events: none;
                    animation: activatePointerEventsSimple 0s 0.15s forwards;
                    z-index: -1;
                    scale: 1;
                    transition: scale 0s 0.2s;
                    ${debug ? `
                      background: repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 10px,
                        rgba(244, 114, 182, 0.15) 10px,
                        rgba(244, 114, 182, 0.15) 20px
                      );
                      border: 2px dashed rgb(244 114 182);
                    ` : ''}
                  }
                `
              }} />

              {/* Debug label para safety triangle simple */}
              {debug && (
                <div
                  className="absolute text-[9px] text-pink-300 font-mono bg-black/70 px-1.5 py-0.5 rounded pointer-events-none z-50"
                  style={{
                    position: "absolute",
                    positionAnchor: "--sidebar-indicator",
                    left: "anchor(right)",
                    marginLeft: "calc((var(--sidebar-width) + var(--sidebar-tooltip-distance)) / 2)",
                    top: "anchor(center)",
                    translate: "-50% -50%",
                    zIndex: 51,
                  }}
                >
                  SAFETY △
                </div>
              )}

              <div
                className="sidebar-tooltip-simple min-w-32"
                style={{
                  position: "absolute",
                  positionAnchor: "--sidebar-indicator",
                  left: "anchor(right)",
                  marginLeft: "calc(var(--sidebar-width) + var(--sidebar-tooltip-distance))",
                  top: "anchor(center)",
                  translate: "0 -50%",
                  zIndex: 50,
                }}
                onMouseLeave={clearHoveredState}
              >
                {/* Inner container con overflow para no cortar ::before */}
                <div
                  className={cn(
                    "px-3 py-2 rounded-lg shadow-lg border border-border",
                    liquidGlass.enableLiquidGlassV2 ? "backdrop-blur-sm" : "bg-card backdrop-blur-sm"
                  )}
                  style={liquidGlass.enableLiquidGlassV2 ? {
                    background: 'rgba(24, 24, 27, 0.2) !important',
                  } : undefined}
                >
                  <span className="text-sm text-foreground pointer-events-auto">{hoveredLabel}</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
})
